import { useEffect, useState } from "react";

export interface OverdueItem {
  reminderId: string;
  title: string;
  body: string;
  reminderType: string;
}

export interface OverdueReminderState {
  overdueCount: number;
  overdueReminderIds: string[];
  overdueItems: OverdueItem[];
  notificationHealth: {
    notAuthorized?: boolean;
    soundDisabled?: boolean;
    criticalAlertsDisabled?: boolean;
    timeSensitiveDisabled?: boolean;
    lockScreenDisabled?: boolean;
  } | null;
}

const initialState: OverdueReminderState = {
  overdueCount: 0,
  overdueReminderIds: [],
  overdueItems: [],
  notificationHealth: null,
};

export function useOverdueReminders(): OverdueReminderState & {
  clearReminder: (reminderId: string) => void;
  openNotificationSettings: () => void;
} {
  const [state, setState] = useState<OverdueReminderState>(initialState);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      try {
        const raw = typeof event.data === "string" ? event.data : "";
        if (!raw) return;
        const data = JSON.parse(raw);

        if (data.type === "reminder_actioned" && data.payload) {
          setState((prev) => ({
            ...prev,
            overdueCount:
              data.payload.overdueCount ?? Math.max(0, prev.overdueCount - 1),
            overdueReminderIds: prev.overdueReminderIds.filter(
              (id) => id !== data.payload.reminderId
            ),
            overdueItems: prev.overdueItems.filter(
              (item) => item.reminderId !== data.payload.reminderId
            ),
          }));
        }

        if (data.type === "reminder_overdue_update" && data.payload) {
          setState((prev) => ({
            ...prev,
            overdueCount: data.payload.overdueCount ?? prev.overdueCount,
            overdueReminderIds:
              data.payload.overdueReminderIds ?? prev.overdueReminderIds,
            overdueItems: data.payload.overdueItems ?? prev.overdueItems,
          }));
        }

        if (data.type === "notification_health" && data.payload) {
          setState((prev) => ({ ...prev, notificationHealth: data.payload }));
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener("message", handler);
    document.addEventListener("message", handler as EventListener);
    return () => {
      window.removeEventListener("message", handler);
      document.removeEventListener("message", handler as EventListener);
    };
  }, []);

  const clearReminder = (reminderId: string) => {
    setState((prev) => ({
      ...prev,
      overdueCount: Math.max(0, prev.overdueCount - 1),
      overdueReminderIds: prev.overdueReminderIds.filter(
        (id) => id !== reminderId
      ),
      overdueItems: prev.overdueItems.filter(
        (item) => item.reminderId !== reminderId
      ),
    }));
  };

  const openNotificationSettings = () => {
    if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ type: "open_notification_settings" })
      );
    }
  };

  return { ...state, clearReminder, openNotificationSettings };
}
